import React, { useCallback, useContext, useEffect, useReducer } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { devicesFormReducer } from './devicesFormReducer';
import {
	AssignDevicesContext,
	assignDevicesInitialState,
	TAssignDevicesCountOfItems,
	TAssignDevicesSortBy,
	TTypeOfModalWindow,
} from './devicesFormContext';
import {
	EditRestaurantContext,
	IEditRestaurantContext,
} from 'modules/CustomersAndRestaurants/Restaurants/EditRestaurant/editRestaurantState/editRestaurantContext';
import { IAssignDevices, IGetPaginatedRestaurantDevices, IRestaurantDevices } from 'types';
import { AssignDevicesActionTypes } from './devicesFormActions';
import { getPaginatedRestaurantDevices } from 'backend/restaurants';
import { AxiosResponse } from 'axios';

const DevicesFormState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(devicesFormReducer, assignDevicesInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { visibleForm } = useContext<IEditRestaurantContext>(EditRestaurantContext);

	const params = new URL(document.location.href).searchParams;
	const restaurantID = params.get('restaurantID');

	const needRestaurantDevicesRequest = visibleForm === 'devicesForm';

	const { refetch, response, loading } = useFetchData<
		AxiosResponse<IRestaurantDevices>,
		IGetPaginatedRestaurantDevices
	>(needRestaurantDevicesRequest ? getPaginatedRestaurantDevices : null, {
		restaurantID: restaurantID || '',
		pageNumber: state.currentPage,
		pageSize: Number(state.countOfItems),
		searchByName: state.searchString ? state.searchString : null,
		showFreeDevices: state.showFreeDevices ? state.showFreeDevices : null,
		sortByHardwareId: state.sortBy === 'HardwareIdA-Z' || state.sortBy === 'HardwareIdZ-A' ? state.sortBy : null,
		sortByTerminalId: state.sortBy === 'TerminalId1-10' || state.sortBy === 'TerminalId10-1' ? state.sortBy : null,
	});

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: AssignDevicesActionTypes.SET_RESTAURANT_DEVICES,
				payload: {
					restaurantDevices: response.data,
				},
			});
		}
	}, [response]);

	const changeSearchString = useCallback((value: string) => {
		dispatch({
			type: AssignDevicesActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	}, []);

	const toggleSetShowFreeDevices = useCallback(() => {
		dispatch({
			type: AssignDevicesActionTypes.TOGGLE_SHOW_FREE_DEVICES,
		});
	}, []);

	const setSortBy = useCallback((sortBy: TAssignDevicesSortBy) => {
		dispatch({
			type: AssignDevicesActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	}, []);

	const setCountOfItems = useCallback((count: TAssignDevicesCountOfItems) => {
		dispatch({
			type: AssignDevicesActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	}, []);

	const changeCurrentPage = useCallback((page: number) => {
		dispatch({
			type: AssignDevicesActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	}, []);

	const toggleDeviceAssignedStatus = useCallback(
		(deviceId: string, status: boolean) => {
			const updatedRestaurantDevices = state.restaurantDevices.items.map((el) =>
				el.id === deviceId ? { ...el, isAssigned: status } : el,
			);
			dispatch({
				type: AssignDevicesActionTypes.SET_RESTAURANT_DEVICES,
				payload: {
					restaurantDevices: { count: state.restaurantDevices.count, items: updatedRestaurantDevices },
				},
			});
		},
		[state.restaurantDevices],
	);

	const toggleSelectAllDevices = useCallback(
		(value: boolean) => {
			const updatedRestaurantDevices = state.restaurantDevices.items.map((el) => ({ ...el, isAssigned: value }));
			dispatch({
				type: AssignDevicesActionTypes.SET_RESTAURANT_DEVICES,
				payload: {
					restaurantDevices: { count: state.restaurantDevices.count, items: updatedRestaurantDevices },
				},
			});
		},
		[state.restaurantDevices],
	);

	const setDataForAssign = useCallback((value: IAssignDevices | null) => {
		dispatch({
			type: AssignDevicesActionTypes.SET_DATA_FOR_ASSIGN,
			payload: {
				dataForAssign: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: AssignDevicesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	useEffect(() => {
		refetch();
	}, [
		state.showFreeDevices,
		state.currentPage,
		state.countOfItems,
		state.searchString,
		state.sortBy,
		needRestaurantDevicesRequest,
	]);

	return (
		<AssignDevicesContext.Provider
			value={{
				changeSearchString,
				toggleSetShowFreeDevices,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				toggleDeviceAssignedStatus,
				toggleSelectAllDevices,
				setDataForAssign,
				changeTypeOfModalWindow,
				restaurantDevices: state.restaurantDevices,
				searchString: state.searchString,
				showFreeDevices: state.showFreeDevices,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				dataForAssign: state.dataForAssign,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</AssignDevicesContext.Provider>
	);
};

export default DevicesFormState;
